import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/customSupabaseClient';
import { toast } from 'sonner';

const PAGE_SIZE = 15;

const DEFAULT_FILTERS = {
  searchTerm: null,
  searchMode: 'fuzzy',
  phase: 'all',
  videoStatus: 'all',
  regionFilter: 'all',
  difficultyFilter: 'all',
  rehabStageFilter: 'all',
  muscleFilter: 'all',
  equipmentFilter: 'all',
  pathTypeFilter: 'all',
  movementTypeFilter: 'all',
  bilateralFilter: 'all',
  executionFilter: 'all',
  usageFilter: 'all',
  demonstratorFilter: 'all',
  showCoachOnly: null,
  sortBy: 'recent_desc',
};

const PRESETS = {
  needsVideo: { videoStatus: 'no_video', sortBy: 'name_asc' },
  unused: { usageFilter: 'unused', sortBy: 'name_asc' },
  popular: { usageFilter: 'popular', sortBy: 'popularity_desc' },
  beginnerFriendly: { difficultyFilter: 'beginner', sortBy: 'name_asc' },
  returnToSport: { rehabStageFilter: 'return_to_sport', sortBy: 'name_asc' },
  needsSync: { videoStatus: 'needs_sync', sortBy: 'recent_desc' },
  highPain: { sortBy: 'usage_desc' },
  noEquipment: { equipmentFilter: 'none', sortBy: 'name_asc' },
};

export const useAdvancedExerciseSearch = () => {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [triggerFetch, setTriggerFetch] = useState(0);
  const [activePreset, setActivePreset] = useState(null);

  const fetchExercises = useCallback(async (newFilters, currentPage) => {
    setLoading(true);
    setError(null);
    try {
      const offset = currentPage * PAGE_SIZE;

      const { data, error: rpcError } = await supabase.rpc('admin_search_exercises_advanced', {
        p_search_term: newFilters.searchTerm || null,
        p_search_mode: newFilters.searchMode,
        p_phase: newFilters.phase,
        p_video_status: newFilters.videoStatus,
        p_region_filter: newFilters.regionFilter,
        p_difficulty_filter: newFilters.difficultyFilter,
        p_rehab_stage_filter: newFilters.rehabStageFilter,
        p_muscle_filter: newFilters.muscleFilter,
        p_equipment_filter: newFilters.equipmentFilter,
        p_path_type_filter: newFilters.pathTypeFilter,
        p_movement_type_filter: newFilters.movementTypeFilter,
        p_bilateral_filter: newFilters.bilateralFilter,
        p_execution_filter: newFilters.executionFilter,
        p_usage_filter: newFilters.usageFilter,
        p_demonstrator_filter: newFilters.demonstratorFilter,
        p_sort_by: newFilters.sortBy,
        p_show_admin_only: newFilters.showCoachOnly,
        p_limit: PAGE_SIZE,
        p_offset: offset,
      });

      if (rpcError) throw rpcError;

      const fetchedData = data || [];
      setExercises(fetchedData);
      setHasMore(fetchedData.length === PAGE_SIZE);

      if (fetchedData.length < PAGE_SIZE) {
        setTotalCount(offset + fetchedData.length);
      } else {
        setTotalCount(offset + fetchedData.length + 1);
      }
    } catch (err) {
      setError('Failed to fetch exercises. Please try again.');
      toast.error("Error fetching exercises", { description: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExercises(filters, page);
  }, [filters, page, fetchExercises, triggerFetch]);

  const updateFilters = useCallback((newFilterValues) => {
    setFilters(prev => ({ ...prev, ...newFilterValues }));
    setPage(0);
    setActivePreset(null);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS });
    setPage(0);
    setActivePreset(null);
  }, []);

  const applyPreset = useCallback((presetId) => {
    const preset = PRESETS[presetId];
    if (!preset) return;
    setFilters({ ...DEFAULT_FILTERS, ...preset });
    setPage(0);
    setActivePreset(presetId);
  }, []);

  const nextPage = useCallback(() => {
    if (hasMore) setPage(p => p + 1);
  }, [hasMore]);

  const previousPage = useCallback(() => {
    setPage(p => Math.max(0, p - 1));
  }, []);

  const search = useCallback(() => {
    setTriggerFetch(c => c + 1);
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'searchTerm' && value) count++;
      else if (key === 'showCoachOnly' && value !== null) count++;
      else if (key !== 'searchTerm' && key !== 'searchMode' && key !== 'sortBy' && key !== 'showCoachOnly' && value !== 'all') count++;
    });
    return count;
  }, [filters]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)), [totalCount]);

  const pagination = useMemo(() => ({
    currentPage: page + 1,
    totalPages,
    nextPage,
    previousPage,
    hasNextPage: hasMore,
    hasPreviousPage: page > 0,
  }), [page, totalPages, nextPage, previousPage, hasMore]);

  return {
    exercises,
    loading,
    error,
    filters,
    updateFilters,
    resetFilters,
    applyPreset,
    activePreset,
    activeFilterCount,
    pagination,
    page,
    setPage,
    nextPage,
    previousPage,
    hasNextPage: hasMore,
    hasPreviousPage: page > 0,
    search,
    totalCount,
  };
};

export const useExerciseFilterOptions = () => {
  const [filterOptions, setFilterOptions] = useState({
    difficulty: [],
    primary_region: [],
    stage_of_rehab: [],
    load_type: [],
    exercise_phase: [],
    exercise_focus_type: [],
    exercise_path: [],
    exercise_mode: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const { data, error } = await supabase.rpc('get_enum_values');
        if (error) throw error;

        const options = data.reduce((acc, { type_name, enum_value }) => {
          if (type_name === 'difficulty_level') acc.difficulty.push(enum_value);
          else if (type_name === 'body_region') acc.primary_region.push(enum_value);
          else if (type_name === 'rehab_stage') acc.stage_of_rehab.push(enum_value);
          else if (type_name === 'load_type_enum') acc.load_type.push(enum_value);
          else if (type_name === 'exercise_phase') acc.exercise_phase.push(enum_value);
          else if (type_name === 'exercise_focus_type') acc.exercise_focus_type.push(enum_value);
          else if (type_name === 'exercise_path') acc.exercise_path.push(enum_value);
          else if (type_name === 'exercise_mode') acc.exercise_mode.push(enum_value);
          return acc;
        }, {
          difficulty: [], primary_region: [], stage_of_rehab: [], load_type: [],
          exercise_phase: [], exercise_focus_type: [], exercise_path: [], exercise_mode: [],
        });

        setFilterOptions(options);
      } catch (err) {
        setError('Failed to load filter options');
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
  }, []);

  return { filterOptions, loading, error };
};

export const useEquipmentOptions = () => {
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data, error } = await supabase
          .from('equipment')
          .select('id, name')
          .order('name', { ascending: true });
        if (error) throw error;
        setEquipmentOptions(data || []);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  return { equipmentOptions, loading };
};

export const useMuscleOptions = () => {
  const [muscleOptions, setMuscleOptions] = useState([]);
  const [muscleGroups, setMuscleGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMuscles = async () => {
      try {
        const { data, error } = await supabase
          .from('muscles')
          .select('muscle_id, name, muscle_group')
          .order('name', { ascending: true });
        if (error) throw error;
        setMuscleOptions(data);
        const uniqueGroups = [...new Set(data.map(m => m.muscle_group).filter(Boolean))];
        setMuscleGroups(uniqueGroups.sort());
      } catch (err) {
        setError('Failed to load muscle options');
      } finally {
        setLoading(false);
      }
    };
    fetchMuscles();
  }, []);

  return { muscleOptions, muscleGroups, loading, error };
};

export const useDemonstratorOptions = () => {
  const [demonstrators, setDemonstrators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDemonstrators = async () => {
      try {
        const { data, error } = await supabase
          .from('athletes')
          .select('id, name')
          .eq('is_active', true)
          .order('name', { ascending: true });
        if (error) throw error;
        setDemonstrators(data || []);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetchDemonstrators();
  }, []);

  return { demonstrators, loading };
};

export const useAthletes = () => {
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAthletes = async () => {
      try {
        const { data, error } = await supabase
          .from('athletes')
          .select('id, name, email, specialization, athlete_photo')
          .eq('is_active', true)
          .order('name', { ascending: true });
        if (error) throw error;
        setAthletes(data);
      } catch (err) {
        setError('Failed to load athletes');
      } finally {
        setLoading(false);
      }
    };
    fetchAthletes();
  }, []);

  return { athletes, loading, error };
};
